import { useApiClient } from '@m43/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CadPrintResource, UserExport } from '@mark43/cad-api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PromiseBuffer } from '../common/promises/promise-buffer';
import {
    CadNextSocketListener,
    createStreamListener,
    useSocketStatus,
    useStream,
} from '../socket/use-stream';
import { getFilePath } from '../common/helpers/files';

interface ExportsApi {
    currentExports: UserExport[];
    deleteExport: (e: UserExport) => void;
    deleteAllExports: (mediaAttachmentsEnabled?: boolean) => void;
    refetchExports: () => void;
    isLoading: boolean;
}

export function useExportsApi(): ExportsApi {
    const apiClient = useApiClient();
    const [exports, setExports] = useState<UserExport[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [promiseBuffer] = useState(() => {
        return new PromiseBuffer<{
            ids: string[];
            exportsStreamMessageType: 'CANCELLED' | 'CREATED' | 'COMPLETED';
        }>({
            delay: 200,
            callback: () => {
                return refetchExports();
            },
        });
    });

    useEffect(() => {
        promiseBuffer.start();
    }, [promiseBuffer]);

    const { refetch: refetchExports } = useQuery(
        ['exports'],
        async () => {
            return apiClient
                .request<CadPrintResource.GetCurrentExports>({
                    method: 'GET',
                    url: `cad_print/exports/`,
                })
                .then((resp) => resp.data.data);
        },
        {
            onSuccess: (exports) => {
                if (exports) {
                    setExports(exports);
                }
                setIsLoading(false);
            },
        }
    );

    const mutation = useMutation(
        ({ exportIds }: { exportIds: string[] }) => {
            return apiClient
                .request<CadPrintResource.DeleteExports>({
                    method: 'DELETE',
                    url: 'cad_print/exports',
                    params: {
                        ids: exportIds,
                    },
                })
                .then((resp) => resp.data.data);
        },
        {
            onSuccess: (exports) => {
                if (exports) {
                    setExports(exports);
                }
            },
        }
    );

    const deleteExport = useCallback(
        function deleteExport(ex: UserExport): void {
            mutation.mutate({ exportIds: [ex.id] });
        },
        [mutation]
    );

    const deleteAllExports = useCallback(
        async function deleteAllExports(mediaAttachmentsEnabled): Promise<void> {
            mutation.mutate({
                exportIds: exports
                    .filter((e) => {
                        const canClear =
                            (e.expirationDate && getFilePath(e.file, mediaAttachmentsEnabled)) ||
                            e.failedPrint;
                        return canClear;
                    })
                    .map((ex) => ex.id),
            });
        },
        [mutation, exports]
    );

    const socketStatusListener: CadNextSocketListener = useMemo(
        () => ({
            onSocketStatusChange: (socketStatusChangeEvent) => {
                if (socketStatusChangeEvent.status === 'CONNECTED') {
                    refetchExports();
                }
            },
            invokeOnInitialize: true,
        }),
        [refetchExports]
    );

    const streamListener = useMemo(() => {
        return createStreamListener({
            streamNames: ['cadExports'],
            onMessage(message) {
                // Determine if we should refresh outside of PromiseBuffer to have updated state
                const command = message.payload.exportsStreamMessageType;
                const ids = message.payload.ids;
                // There should be no case where we have multiple completed exports at once
                const shouldFetchCompletedExport =
                    command === 'COMPLETED' && !!exports.find((e) => e.id === ids?.[0]);

                if (command === 'CANCELLED') {
                    // This means everything was cleared
                    if (ids.length > 1) {
                        setExports([]);
                    } else {
                        setExports((e) => e.filter((ex) => ex.id !== ids?.[0]));
                    }
                }

                if (command === 'CREATED' || shouldFetchCompletedExport) {
                    promiseBuffer.enqueue(message.payload);
                }
            },
        });
    }, [exports, promiseBuffer]);

    useSocketStatus(socketStatusListener);
    useStream(streamListener);

    return {
        currentExports: exports,
        deleteExport,
        deleteAllExports,
        refetchExports,
        isLoading,
    };
}
